import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import Card from '../components/Card'
import LoadingSpinner from '../components/LoadingSpinner'
import { useApi } from '../hooks/useApi'

const FacilitatorBookings = () => {
  const [bookings, setBookings] = useState([])
  const [selectedBooking, setSelectedBooking] = useState(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [filterStatus, setFilterStatus] = useState('all')
  const { loading, error } = useApi()

  useEffect(() => {
    loadBookings()
  }, [])

  const loadBookings = async () => {
    try {
      // In production: const response = await get('/api/bookings/facilitator')
      // Mock data for demonstration
      setTimeout(() => {
        const mockBookings = [
          {
            id: 1,
            sessionDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000),
            sessionType: 'yoga',
            duration: 60,
            status: 'confirmed',
            notes: 'Looking forward to improving my flexibility and reducing lower back pain.',
            createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
            seeker: {
              id: 10,
              name: 'Sarah Martinez',
              email: 'sarah.m@example.com',
              bio: 'Office worker looking to improve posture and reduce stress through yoga practice. Started my wellness journey 2 months ago.',
              targetAilment: 'lower-back-pain',
              wellnessInterest: 'yoga',
              role: 'seeker',
              createdAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000),
            },
            seekerWellness: {
              hasCheckIns: true,
              totalCheckIns: 6,
              latestCheckIn: {
                id: 6,
                physicalGoalScore: 7,
                overallWellbeingScore: 8,
                notes: 'Feeling much better! My back pain has reduced significantly.',
                createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
              },
              averagePhysicalScore: 5.8,
              averageWellbeingScore: 6.5,
              checkInHistory: [
                { id: 1, physicalGoalScore: 4, overallWellbeingScore: 5, createdAt: new Date(Date.now() - 42 * 24 * 60 * 60 * 1000) },
                { id: 2, physicalGoalScore: 5, overallWellbeingScore: 5, createdAt: new Date(Date.now() - 35 * 24 * 60 * 60 * 1000) },
                { id: 3, physicalGoalScore: 5, overallWellbeingScore: 6, createdAt: new Date(Date.now() - 28 * 24 * 60 * 60 * 1000) },
                { id: 4, physicalGoalScore: 6, overallWellbeingScore: 7, createdAt: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000) },
                { id: 5, physicalGoalScore: 6, overallWellbeingScore: 7, createdAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000) },
                { id: 6, physicalGoalScore: 7, overallWellbeingScore: 8, createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) },
              ],
            },
          },
          {
            id: 2,
            sessionDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000),
            sessionType: 'meditation',
            duration: 45,
            status: 'pending',
            notes: 'First time trying meditation. Struggling with anxiety and sleep issues.',
            createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
            seeker: {
              id: 11,
              name: 'Michael Chen',
              email: 'michael.c@example.com',
              bio: 'Tech professional dealing with work stress. New to meditation and mindfulness practices.',
              targetAilment: 'anxiety',
              wellnessInterest: 'meditation',
              role: 'seeker',
              createdAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000),
            },
            seekerWellness: {
              hasCheckIns: true,
              totalCheckIns: 2,
              latestCheckIn: {
                id: 2,
                physicalGoalScore: 5,
                overallWellbeingScore: 4,
                notes: 'Still struggling with racing thoughts at night.',
                createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
              },
              averagePhysicalScore: 4.5,
              averageWellbeingScore: 4.0,
              checkInHistory: [
                { id: 1, physicalGoalScore: 4, overallWellbeingScore: 4, createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000) },
                { id: 2, physicalGoalScore: 5, overallWellbeingScore: 4, createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000) },
              ],
            },
          },
          {
            id: 3,
            sessionDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
            sessionType: 'yoga',
            duration: 90,
            status: 'confirmed',
            notes: 'Experienced practitioner looking to deepen my practice and work on advanced poses.',
            createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
            seeker: {
              id: 12,
              name: 'Emma Thompson',
              email: 'emma.t@example.com',
              bio: 'Yoga enthusiast for 3 years. Currently working on arm balances and inversions. Also interested in the philosophical aspects of yoga.',
              targetAilment: 'strength-building',
              wellnessInterest: 'yoga',
              role: 'seeker',
              createdAt: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000),
            },
            seekerWellness: {
              hasCheckIns: true,
              totalCheckIns: 12,
              latestCheckIn: {
                id: 12,
                physicalGoalScore: 8,
                overallWellbeingScore: 9,
                notes: 'Making great progress! Held crow pose for 30 seconds today.',
                createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
              },
              averagePhysicalScore: 7.5,
              averageWellbeingScore: 8.2,
              checkInHistory: Array.from({ length: 12 }, (_, i) => ({
                id: i + 1,
                physicalGoalScore: Math.min(10, 6 + Math.floor(i / 4)),
                overallWellbeingScore: Math.min(10, 7 + Math.floor(i / 3)),
                createdAt: new Date(Date.now() - (84 - i * 7) * 24 * 60 * 60 * 1000),
              })),
            },
          },
          {
            id: 4,
            sessionDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
            sessionType: 'meditation',
            duration: 30,
            status: 'completed',
            notes: 'Interested in guided meditation for stress relief.',
            createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000),
            seeker: {
              id: 13,
              name: 'David Park',
              email: 'david.p@example.com',
              bio: 'Healthcare worker looking for stress management techniques. Interested in mindfulness for better patient care.',
              targetAilment: 'stress',
              wellnessInterest: 'meditation',
              role: 'seeker',
              createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
            },
            seekerWellness: {
              hasCheckIns: true,
              totalCheckIns: 4,
              latestCheckIn: {
                id: 4,
                physicalGoalScore: 6,
                overallWellbeingScore: 7,
                notes: 'The breathing techniques are really helping during stressful shifts.',
                createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
              },
              averagePhysicalScore: 5.5,
              averageWellbeingScore: 6.0,
              checkInHistory: [
                { id: 1, physicalGoalScore: 5, overallWellbeingScore: 5, createdAt: new Date(Date.now() - 28 * 24 * 60 * 60 * 1000) },
                { id: 2, physicalGoalScore: 5, overallWellbeingScore: 6, createdAt: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000) },
                { id: 3, physicalGoalScore: 6, overallWellbeingScore: 6, createdAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000) },
                { id: 4, physicalGoalScore: 6, overallWellbeingScore: 7, createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) },
              ],
            },
          },
        ]
        setBookings(mockBookings)
      }, 1000)
    } catch (err) {
      console.error('Error loading bookings:', err)
    }
  }

  const handleViewDetails = (booking) => {
    setSelectedBooking(booking)
    setShowDetailModal(true)
  }

  const handleStatusUpdate = async (bookingId, newStatus) => {
    try {
      // In production: await patch(`/api/bookings/${bookingId}/status`, { status: newStatus })
      // Mock update
      setBookings(prev =>
        prev.map(b => (b.id === bookingId ? { ...b, status: newStatus } : b))
      )
      if (selectedBooking?.id === bookingId) {
        setSelectedBooking(prev => ({ ...prev, status: newStatus }))
      }
    } catch (err) {
      console.error('Error updating booking status:', err)
    }
  }

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      confirmed: 'bg-blue-100 text-blue-800 border-blue-200',
      completed: 'bg-green-100 text-green-800 border-green-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200',
    }
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200'
  }

  const getSessionIcon = (sessionType) => {
    const icons = {
      yoga: '🧘‍♀️',
      meditation: '🧘',
      nutrition: '🥗',
      fitness: '💪',
    }
    return icons[sessionType] || '🌟'
  }

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatAilment = (ailment) => {
    if (!ailment) return 'General wellness'
    return ailment
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const filteredBookings = filterStatus === 'all' 
    ? bookings 
    : bookings.filter(b => b.status === filterStatus)

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-20 flex justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-20">
        <Card className="bg-red-50 border-red-200">
          <p className="text-red-600">Error loading bookings: {error}</p>
        </Card>
      </div>
    )
  }

  return (
    <div className="bg-cream min-h-screen py-8">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">My Bookings</h1>
            <p className="text-gray-600">
              View and manage bookings from seekers who want to work with you
            </p>
          </div>

          {/* Filters */}
          <div className="mb-6 flex gap-2 flex-wrap">
            {['all', 'pending', 'confirmed', 'completed', 'cancelled'].map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filterStatus === status
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card>
              <div className="text-center">
                <p className="text-gray-600 text-sm mb-1">Total Bookings</p>
                <p className="text-3xl font-bold text-gray-900">{bookings.length}</p>
              </div>
            </Card>
            <Card>
              <div className="text-center">
                <p className="text-gray-600 text-sm mb-1">Pending</p>
                <p className="text-3xl font-bold text-yellow-600">
                  {bookings.filter(b => b.status === 'pending').length}
                </p>
              </div>
            </Card>
            <Card>
              <div className="text-center">
                <p className="text-gray-600 text-sm mb-1">Confirmed</p>
                <p className="text-3xl font-bold text-blue-600">
                  {bookings.filter(b => b.status === 'confirmed').length}
                </p>
              </div>
            </Card>
            <Card>
              <div className="text-center">
                <p className="text-gray-600 text-sm mb-1">Completed</p>
                <p className="text-3xl font-bold text-green-600">
                  {bookings.filter(b => b.status === 'completed').length}
                </p>
              </div>
            </Card>
          </div>

          {/* Bookings List */}
          {filteredBookings.length === 0 ? (
            <Card>
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📋</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">No Bookings Found</h3>
                <p className="text-gray-600">
                  {filterStatus === 'all'
                    ? "You don't have any bookings yet."
                    : `No ${filterStatus} bookings found.`}
                </p>
              </div>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredBookings.map((booking, index) => (
                <motion.div
                  key={booking.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                    <div className="flex flex-col md:flex-row gap-6">
                      {/* Left Section - Booking Info */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="text-4xl">{getSessionIcon(booking.sessionType)}</div>
                            <div>
                              <h3 className="text-xl font-bold text-gray-900">
                                {booking.sessionType.charAt(0).toUpperCase() + booking.sessionType.slice(1)} Session
                              </h3>
                              <p className="text-sm text-gray-600">{booking.duration} minutes</p>
                            </div>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(booking.status)}`}>
                            {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                          </span>
                        </div>

                        <div className="space-y-2 mb-4">
                          <div className="flex items-center text-gray-700">
                            <span className="text-lg mr-2">📅</span>
                            <span>{formatDate(booking.sessionDate)} at {formatTime(booking.sessionDate)}</span>
                          </div>
                          {booking.notes && (
                            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                              <p className="text-sm text-gray-700">
                                <strong>Seeker&apos;s note:</strong> {booking.notes}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Right Section - Seeker Info */}
                      <div className="md:w-80 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-100">
                        <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                          <span className="text-lg mr-2">👤</span>
                          Seeker Profile
                        </h4>
                        <div className="space-y-3">
                          <div>
                            <p className="font-semibold text-gray-900">{booking.seeker.name}</p>
                            <p className="text-sm text-gray-600">{booking.seeker.email}</p>
                          </div>

                          <div className="bg-white/60 rounded-lg p-3">
                            <p className="text-xs font-medium text-gray-600 mb-1">Physical Goal</p>
                            <p className="font-medium text-indigo-700">
                              {formatAilment(booking.seeker.targetAilment)}
                            </p>
                          </div>

                          {booking.seekerWellness.hasCheckIns && (
                            <div className="space-y-2">
                              <div className="bg-white/60 rounded-lg p-3">
                                <p className="text-xs font-medium text-gray-600 mb-2">Latest Scores</p>
                                <div className="flex justify-between items-center mb-1">
                                  <span className="text-xs text-gray-700">Physical:</span>
                                  <span className="font-bold text-blue-600">
                                    {booking.seekerWellness.latestCheckIn.physicalGoalScore}/10
                                  </span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-xs text-gray-700">Wellbeing:</span>
                                  <span className="font-bold text-green-600">
                                    {booking.seekerWellness.latestCheckIn.overallWellbeingScore}/10
                                  </span>
                                </div>
                              </div>
                              <p className="text-xs text-gray-600 text-center">
                                {booking.seekerWellness.totalCheckIns} check-ins completed
                              </p>
                            </div>
                          )}

                          <button
                            onClick={() => handleViewDetails(booking)}
                            className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium text-sm"
                          >
                            View Full Profile
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    {booking.status === 'pending' && (
                      <div className="mt-4 pt-4 border-t border-gray-200 flex gap-2">
                        <button
                          onClick={() => handleStatusUpdate(booking.id, 'confirmed')}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                        >
                          ✓ Confirm Booking
                        </button>
                        <button
                          onClick={() => handleStatusUpdate(booking.id, 'cancelled')}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                        >
                          ✕ Decline
                        </button>
                      </div>
                    )}

                    {booking.status === 'confirmed' && new Date(booking.sessionDate) < new Date() && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <button
                          onClick={() => handleStatusUpdate(booking.id, 'completed')}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                        >
                          Mark as Completed
                        </button>
                      </div>
                    )}
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Detail Modal */}
        <AnimatePresence>
          {showDetailModal && selectedBooking && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
              onClick={() => setShowDetailModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ type: 'spring', duration: 0.5 }}
                className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="bg-gradient-to-r from-indigo-500 to-purple-500 px-6 py-4 flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-white">
                    Seeker Details - {selectedBooking.seeker.name}
                  </h2>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Modal Content */}
                <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Profile Information */}
                    <div className="space-y-4">
                      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100">
                        <h3 className="font-bold text-gray-900 mb-3 text-lg">👤 Profile Information</h3>
                        <div className="space-y-3">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Name</p>
                            <p className="font-semibold text-gray-900">{selectedBooking.seeker.name}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-600">Email</p>
                            <p className="text-gray-900">{selectedBooking.seeker.email}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-600">Member Since</p>
                            <p className="text-gray-900">
                              {new Date(selectedBooking.seeker.createdAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })}
                            </p>
                          </div>
                          {selectedBooking.seeker.bio && (
                            <div>
                              <p className="text-sm font-medium text-gray-600 mb-1">Bio</p>
                              <p className="text-gray-700 text-sm">{selectedBooking.seeker.bio}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Physical Goal */}
                      <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                        <h3 className="font-bold text-gray-900 mb-2 text-lg">🎯 Physical Goal</h3>
                        <p className="font-medium text-blue-700 text-lg">
                          {formatAilment(selectedBooking.seeker.targetAilment)}
                        </p>
                        <p className="text-sm text-gray-600 mt-2">
                          Primary wellness interest: <span className="font-medium">{selectedBooking.seeker.wellnessInterest}</span>
                        </p>
                      </div>
                    </div>

                    {/* Wellness Progress */}
                    <div className="space-y-4">
                      {selectedBooking.seekerWellness.hasCheckIns ? (
                        <>
                          {/* Latest Scores */}
                          <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-xl p-4 border border-green-200">
                            <h3 className="font-bold text-gray-900 mb-3 text-lg">📊 Current Wellness Status</h3>
                            <div className="space-y-3">
                              <div className="bg-white/60 rounded-lg p-3">
                                <div className="flex items-center justify-between mb-2">
                                  <p className="text-sm font-medium text-gray-700">Physical Goal Score</p>
                                  <p className="text-2xl font-bold text-blue-600">
                                    {selectedBooking.seekerWellness.latestCheckIn.physicalGoalScore}/10
                                  </p>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div
                                    className="bg-blue-600 h-2 rounded-full"
                                    style={{
                                      width: `${selectedBooking.seekerWellness.latestCheckIn.physicalGoalScore * 10}%`,
                                    }}
                                  />
                                </div>
                              </div>

                              <div className="bg-white/60 rounded-lg p-3">
                                <div className="flex items-center justify-between mb-2">
                                  <p className="text-sm font-medium text-gray-700">Overall Wellbeing</p>
                                  <p className="text-2xl font-bold text-green-600">
                                    {selectedBooking.seekerWellness.latestCheckIn.overallWellbeingScore}/10
                                  </p>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div
                                    className="bg-green-600 h-2 rounded-full"
                                    style={{
                                      width: `${selectedBooking.seekerWellness.latestCheckIn.overallWellbeingScore * 10}%`,
                                    }}
                                  />
                                </div>
                              </div>

                              {selectedBooking.seekerWellness.latestCheckIn.notes && (
                                <div className="bg-white/60 rounded-lg p-3">
                                  <p className="text-xs font-medium text-gray-600 mb-1">Latest Note</p>
                                  <p className="text-sm text-gray-700 italic">
                                    &quot;{selectedBooking.seekerWellness.latestCheckIn.notes}&quot;
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    {new Date(selectedBooking.seekerWellness.latestCheckIn.createdAt).toLocaleDateString()}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Progress Summary */}
                          <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                            <h3 className="font-bold text-gray-900 mb-3 text-lg">📈 Progress Summary</h3>
                            <div className="grid grid-cols-2 gap-3">
                              <div className="bg-white/60 rounded-lg p-3 text-center">
                                <p className="text-xs font-medium text-gray-600">Total Check-ins</p>
                                <p className="text-2xl font-bold text-purple-600">
                                  {selectedBooking.seekerWellness.totalCheckIns}
                                </p>
                              </div>
                              <div className="bg-white/60 rounded-lg p-3 text-center">
                                <p className="text-xs font-medium text-gray-600">Avg Physical</p>
                                <p className="text-2xl font-bold text-blue-600">
                                  {selectedBooking.seekerWellness.averagePhysicalScore}
                                </p>
                              </div>
                              <div className="bg-white/60 rounded-lg p-3 text-center col-span-2">
                                <p className="text-xs font-medium text-gray-600">Avg Wellbeing</p>
                                <p className="text-2xl font-bold text-green-600">
                                  {selectedBooking.seekerWellness.averageWellbeingScore}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Progress History */}
                          {selectedBooking.seekerWellness.checkInHistory.length > 0 && (
                            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                              <h3 className="font-bold text-gray-900 mb-3 text-lg">📅 Check-in History</h3>
                              <div className="space-y-2 max-h-64 overflow-y-auto">
                                {selectedBooking.seekerWellness.checkInHistory
                                  .slice()
                                  .reverse()
                                  .map((checkIn, index) => (
                                    <div key={checkIn.id} className="bg-white rounded-lg p-3 text-sm">
                                      <div className="flex justify-between items-center mb-1">
                                        <span className="font-medium text-gray-700">
                                          {new Date(checkIn.createdAt).toLocaleDateString()}
                                        </span>
                                        <div className="flex gap-2">
                                          <span className="text-blue-600 font-bold">P: {checkIn.physicalGoalScore}</span>
                                          <span className="text-green-600 font-bold">W: {checkIn.overallWellbeingScore}</span>
                                        </div>
                                      </div>
                                      {index === 0 && (
                                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                                          Latest
                                        </span>
                                      )}
                                    </div>
                                  ))}
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
                          <div className="text-5xl mb-3">📊</div>
                          <h3 className="font-bold text-gray-900 mb-2">No Check-ins Yet</h3>
                          <p className="text-sm text-gray-600">
                            This seeker hasn&apos;t completed any wellness check-ins yet.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Booking Details Section */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h3 className="font-bold text-gray-900 mb-3 text-lg">🗓️ Session Details</h3>
                    <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-xl p-4 border border-primary-200">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Session Type</p>
                          <p className="font-semibold text-gray-900">
                            {getSessionIcon(selectedBooking.sessionType)}{' '}
                            {selectedBooking.sessionType.charAt(0).toUpperCase() + selectedBooking.sessionType.slice(1)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-600">Date & Time</p>
                          <p className="font-semibold text-gray-900 text-sm">
                            {formatDate(selectedBooking.sessionDate)}
                          </p>
                          <p className="text-sm text-gray-700">{formatTime(selectedBooking.sessionDate)}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-600">Duration</p>
                          <p className="font-semibold text-gray-900">{selectedBooking.duration} minutes</p>
                        </div>
                      </div>
                      {selectedBooking.notes && (
                        <div className="mt-4 pt-4 border-t border-blue-200">
                          <p className="text-sm font-medium text-gray-600 mb-1">Seeker&apos;s Message</p>
                          <p className="text-gray-700">{selectedBooking.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="mt-6 flex gap-3">
                    {selectedBooking.status === 'pending' && (
                      <>
                        <button
                          onClick={() => {
                            handleStatusUpdate(selectedBooking.id, 'confirmed')
                            setShowDetailModal(false)
                          }}
                          className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                        >
                          ✓ Confirm Booking
                        </button>
                        <button
                          onClick={() => {
                            handleStatusUpdate(selectedBooking.id, 'cancelled')
                            setShowDetailModal(false)
                          }}
                          className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                        >
                          ✕ Decline Booking
                        </button>
                      </>
                    )}
                    {selectedBooking.status === 'confirmed' && new Date(selectedBooking.sessionDate) < new Date() && (
                      <button
                        onClick={() => {
                          handleStatusUpdate(selectedBooking.id, 'completed')
                          setShowDetailModal(false)
                        }}
                        className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                      >
                        Mark as Completed
                      </button>
                    )}
                    <button
                      onClick={() => setShowDetailModal(false)}
                      className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

export default FacilitatorBookings

